import React from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BadgeVariant =
  | 'green'
  | 'red'
  | 'amber'
  | 'gray'
  | 'blue'
  | 'purple'   // nuevo: roles premium, admin
  | 'teal'     // nuevo: exportación, guías de remisión, retenciones
  | 'subtle';  // nuevo: metadata de bajo énfasis, estados secundarios

// `auto`: sm (22 px, texto 11 px) por debajo de 1536 px y 24 px con texto 12 px desde 1536 px. Pensado para encabezados de página.
export type BadgeSize  = 'sm' | 'md' | 'auto';
export type BadgeShape = 'pill' | 'rounded';

export interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  color?: string;               // Custom hex (e.g. "#8B5CF6") — tiene prioridad sobre variant
  outline?: boolean;            // sin fondo, solo borde y texto
  noDot?: boolean;              // oculta el dot indicator
  iconLeft?: React.ReactNode;   // ícono antes del texto — reemplaza al dot cuando se usa
  iconRight?: React.ReactNode;  // ícono después del texto
  size?: BadgeSize;             // sm (default, tablas) | md (28px, cards) | auto (22px → 24px desde 1536px, PageHeader)
  shape?: BadgeShape;           // pill (default) | rounded (códigos SUNAT, tags)
  className?: string;
}

// ─── Style maps ───────────────────────────────────────────────────────────────

const solidStyles: Record<BadgeVariant, string> = {
  green:  'bg-[var(--success-bg)] text-[var(--success)] border-[var(--success-border)]',
  red:    'bg-[var(--error-bg)] text-[var(--error)] border-[var(--error-border)]',
  amber:  'bg-[var(--warning-bg)] text-[var(--warning)] border-[var(--warning-border)]',
  gray:   'bg-surface-1 text-tertiary border-default',
  blue:   'bg-[var(--info-bg)] text-[var(--info)] border-[var(--info-border)]',
  purple: 'bg-[var(--purple-bg,#f3e8ff)] text-[var(--purple,#7c3aed)] border-[var(--purple-border,#c4b5fd)]',
  teal:   'bg-[var(--teal-bg,#ccfbf1)] text-[var(--teal,#0d9488)] border-[var(--teal-border,#5eead4)]',
  subtle: 'bg-surface-0 text-quaternary border-[var(--border-subtle,#F1F5F9)]',
};

const outlineStyles: Record<BadgeVariant, string> = {
  green:  'bg-transparent text-[var(--success)] border-[var(--success-border)]',
  red:    'bg-transparent text-[var(--error)] border-[var(--error-border)]',
  amber:  'bg-transparent text-[var(--warning)] border-[var(--warning-border)]',
  gray:   'bg-transparent text-tertiary border-default',
  blue:   'bg-transparent text-[var(--info)] border-[var(--info-border)]',
  purple: 'bg-transparent text-[var(--purple,#7c3aed)] border-[var(--purple-border,#c4b5fd)]',
  teal:   'bg-transparent text-[var(--teal,#0d9488)] border-[var(--teal-border,#5eead4)]',
  subtle: 'bg-transparent text-quaternary border-[var(--border-subtle,#F1F5F9)]',
};

const dotStyles: Record<BadgeVariant, string> = {
  green:  'bg-[var(--success)]',
  red:    'bg-[var(--error)]',
  amber:  'bg-[var(--warning)]',
  gray:   'bg-tertiary',
  blue:   'bg-[var(--info)]',
  purple: 'bg-[var(--purple,#7c3aed)]',
  teal:   'bg-[var(--teal,#0d9488)]',
  subtle: 'bg-[var(--border-default,#CBD5E1)]',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-[11px] px-2 h-[22px] leading-none',
  md: 'text-[12px] px-3 h-7',   // h-7 = 28px
  auto: 'text-[11px] px-2 h-[22px] leading-none 2xl:text-[12px] 2xl:h-6',
};

const shapeStyles: Record<BadgeShape, string> = {
  pill:    'rounded-full',
  rounded: 'rounded-[6px]',
};

const iconSizeStyles: Record<BadgeSize, string> = {
  sm: '[&>svg]:w-3 [&>svg]:h-3',     // 12px
  md: '[&>svg]:w-3.5 [&>svg]:h-3.5', // 14px
  auto: '[&>svg]:w-3 [&>svg]:h-3 2xl:[&>svg]:w-3.5 2xl:[&>svg]:h-3.5',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Badge({
  children,
  variant = 'gray',
  color,
  outline = false,
  noDot = false,
  iconLeft,
  iconRight,
  size = 'sm',
  shape = 'pill',
  className = '',
}: BadgeProps) {
  // Cuando hay iconLeft, el dot se omite automáticamente (el ícono lo reemplaza)
  const showDot = !noDot && !iconLeft;

  const baseClasses = [
    'inline-flex items-center gap-1 font-medium whitespace-nowrap border',
    sizeStyles[size],
    shapeStyles[shape],
    className,
  ].filter(Boolean).join(' ');

  // ── Custom hex color ──────────────────────────────────────────────────────
  if (color) {
    const rgb = hexToRgb(color);
    if (rgb) {
      const bgColor     = outline ? 'transparent' : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`;
      const borderColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${outline ? 0.4 : 0.2})`;

      return (
        <span
          className={baseClasses}
          style={{ backgroundColor: bgColor, borderColor, color }}
        >
          {showDot && (
            <span
              className="w-[5px] h-[5px] rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
          )}
          {iconLeft && (
            <span className={`flex-shrink-0 flex items-center ${iconSizeStyles[size]}`}>{iconLeft}</span>
          )}
          {children}
          {iconRight && (
            <span className={`flex-shrink-0 flex items-center ${iconSizeStyles[size]}`}>{iconRight}</span>
          )}
        </span>
      );
    }
  }

  // ── Semantic variant ──────────────────────────────────────────────────────
  const colorClasses = outline ? outlineStyles[variant] : solidStyles[variant];

  return (
    <span className={`${baseClasses} ${colorClasses}`}>
      {showDot && (
        <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${dotStyles[variant]}`} />
      )}
      {iconLeft && (
        <span className={`flex-shrink-0 flex items-center ${iconSizeStyles[size]}`}>{iconLeft}</span>
      )}
      {children}
      {iconRight && (
        <span className={`flex-shrink-0 flex items-center ${iconSizeStyles[size]}`}>{iconRight}</span>
      )}
    </span>
  );
}

Badge.displayName = 'Badge';
